import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Plus, ExternalLink, FileText, Layers, Copy, LayoutTemplate } from 'lucide-react'
import { useBuilderPages } from './hooks/useBuilderPages'
import { BuilderCreateModal } from './BuilderCreateModal'
import { BuilderTemplatePicker } from './BuilderTemplatePicker'
import { BuilderDuplicateModal } from './BuilderDuplicateModal'
import { BuilderTopHeader } from './BuilderNavTabs'

/**
 * Page d'accueil du Page Builder.
 * Affiche la liste des pages existantes ou un empty state pour la première visite.
 *
 * DEUX GESTES DE CRÉATION, jamais fondus en un : une page VIERGE (un titre
 * suffit) ou une page À PARTIR D'UN MODÈLE (sélecteur avec aperçu réel). Le
 * second est le chemin par lequel un site pose son header et son footer : le
 * modèle les porte, et la duplication les recopie — ou les garde COMMUNS quand
 * ils y sont posés en « bloc existant ».
 *
 * `?create=<mode>&site=<gid>` ouvre d'emblée l'écran voulu, la page à créer
 * étant rattachée à ce site : ce sont les boutons de l'écran d'un site
 * (`/sites/:id`), qui n'a pas de quoi générer un slug, en vérifier la
 * disponibilité puis rediriger vers l'URL retournée — le builder porte déjà tout
 * cela. Même mécanique que le `?edit=1` de BuilderShell.
 *
 *   blank        page vierge (`create=1` reste accepté : l'URL historique)
 *   template     sélecteur de gabarits
 *   new-template gabarit vierge
 */
const CREATE_MODES = { '1': 'blank', blank: 'blank', template: 'template', 'new-template': 'new-template' }

export const BuilderHome = ({ embedded = false }) => {
  // Sous le menu orbit (`embedded`, routes `withMenu`) : remplir le `main`
  // d'OrbitChrome plutôt qu'imposer 100vh, sinon la page dépasse d'une hauteur
  // de barre de menu. Standalone : plein écran comme avant.
  const shellClass = `${embedded ? 'flex-1' : 'min-h-screen'} bg-gray-50`

  const [searchParams, setSearchParams] = useSearchParams()
  // Lus au PREMIER rendu : le nettoyage de l'URL ci-dessous les efface aussitôt,
  // pour qu'un rafraîchissement ne rouvre pas l'écran de création.
  const [createSiteId] = useState(() => searchParams.get('site') || null)
  // `null` = aucun écran de création ouvert.
  const [createMode, setCreateMode] = useState(() => CREATE_MODES[searchParams.get('create')] ?? null)
  const [duplicatePage, setDuplicatePage] = useState(null) // page à dupliquer
  const navigate = useNavigate()
  const { pages, loading } = useBuilderPages(createSiteId)
  // Le modèle par défaut du site, à présélectionner dans le sélecteur. Simple
  // confort : son absence laisse le sélecteur parfaitement utilisable.
  const [defaultModelNid, setDefaultModelNid] = useState(null)

  useEffect(() => {
    if (searchParams.get('create')) {
      // `replace` : l'entrée d'historique du site d'origine est conservée, donc
      // « Annuler » peut y ramener (cf. handleCloseCreate).
      setSearchParams({}, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Le site désigne son modèle par défaut par un CHAMP (`default_model`), lu ici
  // via l'API générique — donc déjà filtrée par les droits. Enveloppe Syncable
  // pour qui peut écrire le site, objet plat sinon (même piège que
  // BuilderPageAddressPanel).
  useEffect(() => {
    if (!createSiteId) return undefined
    let cancelled = false
    fetch(`/decoupled-api/group/site/${createSiteId}?display=decoupled`, { credentials: 'include' })
      .then(res => (res.ok ? res.json() : null))
      .then(payload => {
        if (cancelled || !payload) return
        const site = payload.data ?? payload
        // `defaultModelNid` est calculé en lecture par orbit_site : un entier,
        // comparable au `nid` que sert /api/builder/pages. Le CHAMP, lui, sort
        // en chaîne — même piège que `homepageNid` dans site.yaml.
        setDefaultModelNid(site?.defaultModelNid ?? null)
      })
      .catch(() => { /* présélection optionnelle */ })
    return () => { cancelled = true }
  }, [createSiteId])

  // Renoncer à la création : retour à l'écran du site quand on en vient, sinon
  // simple fermeture. La création réussie, elle, ne passe pas par ici — elle
  // navigue vers le builder de la page créée.
  const handleCloseCreate = () => {
    setCreateMode(null)
    if (createSiteId) navigate(-1)
  }

  if (loading) {
    return (
      <div className={`${shellClass} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-teal-700" />
      </div>
    )
  }

  return (
    <div className={shellClass}>
      <BuilderTopHeader
        rightSlot={
          pages.length > 0 && (
            <CreateButtons onCreate={setCreateMode} size="sm" />
          )
        }
      />

      {/* Contenu */}
      <main className="max-w-5xl mx-auto px-6 py-10">
        {pages.length === 0 ? (
          <EmptyState onCreate={setCreateMode} />
        ) : (
          <PageList
            pages={pages}
            onCreate={setCreateMode}
            onOpen={(slug) => navigate(`/builder/${slug}`)}
            onDuplicate={(page) => setDuplicatePage(page)}
          />
        )}
      </main>

      {(createMode === 'blank' || createMode === 'new-template') && (
        <BuilderCreateModal
          onClose={handleCloseCreate}
          siteId={createSiteId}
          asTemplate={createMode === 'new-template'}
          defaultModelNid={defaultModelNid}
        />
      )}
      {createMode === 'template' && (
        <BuilderTemplatePicker
          onClose={handleCloseCreate}
          siteId={createSiteId}
          defaultModelNid={defaultModelNid}
        />
      )}
      {duplicatePage && (
        <BuilderDuplicateModal page={duplicatePage} onClose={() => setDuplicatePage(null)} />
      )}
    </div>
  )
}

/**
 * Les DEUX gestes de création, côte à côte et jamais fondus : « Page vierge »
 * n'ouvre qu'un champ titre, « À partir d'un modèle » ouvre le sélecteur avec
 * aperçu. Les proposer ensemble est ce qui rend les modèles visibles — cachés
 * derrière un seul bouton, personne ne saurait qu'un site en a.
 */
const CreateButtons = ({ onCreate, size = '' }) => {
  const cls = size === 'sm' ? 'btn btn-sm' : 'btn'
  return (
    <div className="flex items-center gap-2">
      <button type="button" onClick={() => onCreate('blank')} className={`${cls} btn-secondary`}>
        <Plus size={size === 'sm' ? 14 : 16} aria-hidden="true" />
        Page vierge
      </button>
      <button type="button" onClick={() => onCreate('template')} className={`${cls} btn-primary`}>
        <LayoutTemplate size={size === 'sm' ? 14 : 16} aria-hidden="true" />
        À partir d&apos;un gabarit
      </button>
    </div>
  )
}

const EmptyState = ({ onCreate }) => (
  <div className="flex flex-col items-center justify-center py-24 text-center">
    <div className="w-16 h-16 bg-iris-50 rounded-2xl flex items-center justify-center mb-6">
      <FileText size={28} className="text-iris-600" aria-hidden="true" />
    </div>
    <h1 className="text-2xl font-semibold text-slate-800 mb-2">
      Bienvenue dans le Page Builder
    </h1>
    <p className="text-sm text-slate-500 max-w-sm mb-8">
      Créez votre première page — vierge, ou à partir d&apos;un gabarit de site
      qui apporte déjà son header et son footer.
    </p>
    <CreateButtons onCreate={onCreate} />
  </div>
)

const PageList = ({ pages, onCreate, onOpen, onDuplicate }) => (
  <div>
    <div className="flex items-center justify-between mb-6">
      <h1 className="text-lg font-semibold text-slate-800">Mes pages</h1>
      <CreateButtons onCreate={onCreate} size="sm" />
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {pages.map(page => (
        <PageCard key={page.slug} page={page} onOpen={onOpen} onDuplicate={onDuplicate} />
      ))}

      {/* Carte « Nouvelle page » — le geste courant (vierge) ; partir d'un
          modèle reste dans la barre, où les deux gestes se comparent. */}
      <button
        type="button"
        onClick={() => onCreate('blank')}
        className="flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed border-slate-200 text-slate-400 hover:border-iris-400 hover:text-iris-600 hover:bg-iris-50 transition-all min-h-[120px]"
      >
        <Plus size={22} aria-hidden="true" />
        <span className="text-sm font-medium">Page vierge</span>
      </button>
    </div>
  </div>
)

const PageCard = ({ page, onOpen, onDuplicate }) => (
  <div
    className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-3 hover:border-teal-300 hover:shadow-sm transition-all cursor-pointer group"
    onClick={() => onOpen(page.slug)}
  >
    {/* Thumbnail placeholder */}
    <div className="w-full h-24 bg-gray-50 rounded-lg flex items-center justify-center border border-gray-100">
      <Layers size={20} className="text-gray-300" />
    </div>

    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-800 group-hover:text-teal-700 truncate transition-colors">
        {page.title || page.slug}
      </p>
      <p className="text-xs text-gray-400 mt-0.5 truncate font-mono">/builder/{page.slug}</p>
    </div>

    <div className="flex items-center gap-3">
      <button
        onClick={(e) => { e.stopPropagation(); onOpen(page.slug) }}
        className="flex items-center gap-1.5 text-xs font-medium text-teal-700 hover:text-teal-900 transition-colors"
      >
        <ExternalLink size={12} />
        Open
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onDuplicate(page) }}
        className="flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-gray-700 transition-colors ml-auto"
        title="Duplicate this page"
      >
        <Copy size={12} />
        Duplicate
      </button>
    </div>
  </div>
)
